/**
 * Blood Pressure & Pulse Trend Chart
 * Styled with vintage medical ledger & paper document aesthetics.
 * Uses Recharts to render interactive time-series visualizations.
 */

import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  TooltipProps,
} from 'recharts';
import { BloodPressureRecord } from '@/types/record';
import { classifyBP } from '@/utils/bpClassification';
import { calculateMAP, calculatePulsePressure } from '@/utils/calculations';
import { StampBadge } from '@/components/common/StampBadge';

export interface TrendChartProps {
  records: BloodPressureRecord[];
  height?: number;
  className?: string;
}

type ChartMode = 'line' | 'area';

interface ChartPoint {
  id: number;
  rawDate: string;
  rawTime: string;
  displayLabel: string;
  fullDateLabel: string;
  systolic: number;
  diastolic: number;
  pulse: number;
  pulsePressure: number;
  map: number;
  notes?: string | null;
  arm?: string;
  record: BloodPressureRecord;
}

export const TrendChart: React.FC<TrendChartProps> = ({
  records,
  height = 340,
  className = '',
}) => {
  // Metric display toggles
  const [showSystolic, setShowSystolic] = useState<boolean>(true);
  const [showDiastolic, setShowDiastolic] = useState<boolean>(true);
  const [showPulse, setShowPulse] = useState<boolean>(true);
  const [showRefLines, setShowRefLines] = useState<boolean>(true);
  const [chartMode, setChartMode] = useState<ChartMode>('line');

  // Sort records chronologically ascending for time-series display
  const chartData = useMemo<ChartPoint[]>(() => {
    if (!records || records.length === 0) return [];

    const copy = [...records].sort((a, b) => {
      const timeA = a.measured_at || `${a.measured_date}T${a.measured_time}`;
      const timeB = b.measured_at || `${b.measured_date}T${b.measured_time}`;
      return timeA.localeCompare(timeB);
    });

    return copy.map((r) => {
      const monthDay = r.measured_date.length >= 10 ? r.measured_date.slice(5) : r.measured_date;
      const displayLabel = `${monthDay} ${r.measured_time}`;
      const pp = calculatePulsePressure(r.systolic, r.diastolic);
      const mapVal = calculateMAP(r.systolic, r.diastolic);

      return {
        id: r.id,
        rawDate: r.measured_date,
        rawTime: r.measured_time,
        displayLabel,
        fullDateLabel: `${r.measured_date} ${r.measured_time}`,
        systolic: r.systolic,
        diastolic: r.diastolic,
        pulse: r.pulse,
        pulsePressure: pp,
        map: mapVal,
        notes: r.notes,
        arm: r.arm,
        record: r,
      };
    });
  }, [records]);

  // Compute dynamic Y-axis domain
  const yDomain = useMemo<[number, number]>(() => {
    if (chartData.length === 0) return [40, 180];
    let min = Infinity;
    let max = -Infinity;

    for (const d of chartData) {
      if (showSystolic && d.systolic > max) max = d.systolic;
      if (showDiastolic && d.diastolic < min) min = d.diastolic;
      if (showPulse) {
        if (d.pulse < min) min = d.pulse;
        if (d.pulse > max) max = d.pulse;
      }
    }

    if (!isFinite(min)) min = 50;
    if (!isFinite(max)) max = 150;

    // Expand margin slightly and snap to 10
    const roundedMin = Math.max(30, Math.floor((min - 10) / 10) * 10);
    const roundedMax = Math.min(260, Math.ceil((max + 15) / 10) * 10);
    return [roundedMin, roundedMax];
  }, [chartData, showSystolic, showDiastolic, showPulse]);

  // Custom Editorial Paper Tooltip
  const renderCustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0]?.payload as ChartPoint;
    if (!data) return null;

    const category = classifyBP(data.systolic, data.diastolic);
    let armLabel = '未指定';
    if (data.arm === 'left') armLabel = '左手';
    if (data.arm === 'right') armLabel = '右手';

    return (
      <div className="bg-white border-2 border-ink-900 p-3.5 rounded-xs shadow-[3px_3px_0px_0px_rgba(28,25,23,0.15)] font-serif text-xs min-w-[220px] max-w-[280px]">
        {/* Timestamp & Arm */}
        <div className="flex items-center justify-between pb-1.5 mb-2 border-b border-paper-400 text-ink-600 font-mono text-[11px]">
          <span>{data.fullDateLabel}</span>
          <span className="bg-paper-200 px-1.5 py-0.2 border border-paper-400 rounded-xs text-[10px]">
            {armLabel}
          </span>
        </div>

        {/* AHA Category Stamp Badge */}
        <div className="mb-2">
          <StampBadge category={category.category} size="sm" />
        </div>

        {/* Numeric Blood Pressure Indicators */}
        <div className="space-y-1 font-mono">
          <div className="flex items-center justify-between">
            <span className="text-red-800 font-bold flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-red-800 rounded-full inline-block" />
              收縮壓 (SYS):
            </span>
            <span className="text-red-950 font-bold text-sm">{data.systolic} <span className="text-[10px] text-ink-500 font-serif">mmHg</span></span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-blue-900 font-bold flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-blue-900 rounded-full inline-block" />
              舒張壓 (DIA):
            </span>
            <span className="text-blue-950 font-bold text-sm">{data.diastolic} <span className="text-[10px] text-ink-500 font-serif">mmHg</span></span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-emerald-800 font-bold flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-emerald-700 rounded-full inline-block" />
              心跳脈搏 (PULSE):
            </span>
            <span className="text-emerald-950 font-bold text-sm">{data.pulse} <span className="text-[10px] text-ink-500 font-serif">bpm</span></span>
          </div>
        </div>

        {/* Derived Clinical Metrics */}
        <div className="mt-2 pt-2 border-t border-paper-300 grid grid-cols-2 gap-1 text-[11px] font-mono text-ink-600 bg-paper-100 p-1.5 rounded-xs">
          <div>
            脈壓差: <strong>{data.pulsePressure}</strong>
          </div>
          <div>
            MAP: <strong>{data.map}</strong>
          </div>
        </div>

        {/* Notes (if present) */}
        {data.notes && (
          <div className="mt-2 pt-1.5 border-t border-dashed border-paper-300 text-[11px] text-ink-800 italic bg-paper-100/60 p-1 rounded-xs">
            備註: 「{data.notes}」
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`
        bg-paper-100 border border-paper-400 rounded-xs shadow-paper p-4 sm:p-5
        ${className}
      `}
    >
      {/* Chart Control Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 mb-3 border-b border-paper-300">
        <div>
          <h3 className="font-serif font-bold text-sm sm:text-base text-ink-900 uppercase tracking-wider">
            血壓與心跳時間序列趨勢 (Clinical BP Trend)
          </h3>
          <p className="font-serif text-[11px] text-ink-500">
            AHA 臨床標準警戒線 • 收縮壓 (紅色) / 舒張壓 (藍色) / 心跳 (綠色)
          </p>
        </div>

        {/* Controls: Series Toggles & Mode Switch */}
        <div className="flex items-center flex-wrap gap-2 text-xs font-serif">
          {/* Systolic Toggle */}
          <button
            type="button"
            onClick={() => setShowSystolic((prev) => !prev)}
            className={`
              px-2.5 py-1 rounded-xs border font-medium transition-colors flex items-center gap-1.5
              ${
                showSystolic
                  ? 'bg-red-50 text-red-900 border-red-700 font-bold'
                  : 'bg-paper-200 text-ink-400 border-paper-300 line-through'
              }
            `}
          >
            <span className="w-2 h-2 rounded-full bg-red-700" />
            收縮壓 (SYS)
          </button>

          {/* Diastolic Toggle */}
          <button
            type="button"
            onClick={() => setShowDiastolic((prev) => !prev)}
            className={`
              px-2.5 py-1 rounded-xs border font-medium transition-colors flex items-center gap-1.5
              ${
                showDiastolic
                  ? 'bg-blue-50 text-blue-900 border-blue-900 font-bold'
                  : 'bg-paper-200 text-ink-400 border-paper-300 line-through'
              }
            `}
          >
            <span className="w-2 h-2 rounded-full bg-blue-900" />
            舒張壓 (DIA)
          </button>

          {/* Pulse Toggle */}
          <button
            type="button"
            onClick={() => setShowPulse((prev) => !prev)}
            className={`
              px-2.5 py-1 rounded-xs border font-medium transition-colors flex items-center gap-1.5
              ${
                showPulse
                  ? 'bg-emerald-50 text-emerald-900 border-emerald-700 font-bold'
                  : 'bg-paper-200 text-ink-400 border-paper-300 line-through'
              }
            `}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-700" />
            心跳 (PULSE)
          </button>

          {/* AHA Reference Lines Toggle */}
          <button
            type="button"
            onClick={() => setShowRefLines((prev) => !prev)}
            className={`
              px-2 py-1 rounded-xs border text-[11px] transition-colors
              ${
                showRefLines
                  ? 'bg-paper-300 text-ink-900 border-ink-700 font-bold'
                  : 'bg-paper-100 text-ink-400 border-paper-300'
              }
            `}
          >
            {showRefLines ? '✓ 警戒基準線' : '○ 隱藏基準線'}
          </button>

          {/* Chart Style Toggle */}
          <div className="flex items-center border border-paper-400 rounded-xs overflow-hidden">
            <button
              type="button"
              onClick={() => setChartMode('line')}
              className={`px-2 py-1 text-[11px] ${
                chartMode === 'line'
                  ? 'bg-ink-900 text-paper-100 font-bold'
                  : 'bg-paper-100 text-ink-700 hover:bg-paper-200'
              }`}
            >
              折線
            </button>
            <button
              type="button"
              onClick={() => setChartMode('area')}
              className={`px-2 py-1 text-[11px] ${
                chartMode === 'area'
                  ? 'bg-ink-900 text-paper-100 font-bold'
                  : 'bg-paper-100 text-ink-700 hover:bg-paper-200'
              }`}
            >
              區域
            </button>
          </div>
        </div>
      </div>

      {/* Chart Canvas Area */}
      {chartData.length === 0 ? (
        <div
          style={{ height }}
          className="flex flex-col items-center justify-center bg-paper-200/50 border border-dashed border-paper-400 rounded-xs text-center p-6"
        >
          <div className="w-12 h-12 rounded-full bg-paper-300 border border-paper-400 flex items-center justify-center text-ink-400 text-xl font-serif mb-2">
            📈
          </div>
          <h4 className="font-serif font-bold text-sm text-ink-800">
            尚無足夠之臨床量測紀錄繪製趨勢圖表
          </h4>
          <p className="font-serif text-xs text-ink-500 mt-1 max-w-sm">
            請在右側或下方表單登記血壓數據，系統將自動繪製收縮壓、舒張壓與脈搏之時間序列圖表。
          </p>
        </div>
      ) : (
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">
            {chartMode === 'line' ? (
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 16, left: -10, bottom: 6 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#d6cebe"
                  vertical={false}
                />
                <XAxis
                  dataKey="displayLabel"
                  stroke="#78716c"
                  tick={{ fontSize: 11, fontFamily: 'monospace', fill: '#44403c' }}
                  tickLine={{ stroke: '#a8a29e' }}
                  axisLine={{ stroke: '#78716c' }}
                />
                <YAxis
                  domain={yDomain}
                  stroke="#78716c"
                  tick={{ fontSize: 11, fontFamily: 'monospace', fill: '#44403c' }}
                  tickLine={{ stroke: '#a8a29e' }}
                  axisLine={{ stroke: '#78716c' }}
                  unit=" "
                />
                <Tooltip content={renderCustomTooltip} />

                {/* AHA Reference Lines */}
                {showRefLines && (
                  <>
                    {/* Normal threshold: SBP 120 */}
                    <ReferenceLine
                      y={120}
                      stroke="#ca8a04"
                      strokeDasharray="4 4"
                      strokeWidth={1}
                      label={{
                        value: '偏高分界 (120)',
                        position: 'insideTopRight',
                        fill: '#854d0e',
                        fontSize: 10,
                        fontFamily: 'serif',
                      }}
                    />
                    {/* Stage 1 threshold: SBP 130 */}
                    <ReferenceLine
                      y={130}
                      stroke="#ea580c"
                      strokeDasharray="4 4"
                      strokeWidth={1}
                      label={{
                        value: '一期高壓 (130)',
                        position: 'insideTopRight',
                        fill: '#9a3412',
                        fontSize: 10,
                        fontFamily: 'serif',
                      }}
                    />
                    {/* Stage 2 threshold: SBP 140 */}
                    <ReferenceLine
                      y={140}
                      stroke="#dc2626"
                      strokeDasharray="4 4"
                      strokeWidth={1.2}
                      label={{
                        value: '二期高壓 (140)',
                        position: 'insideTopRight',
                        fill: '#991b1b',
                        fontSize: 10,
                        fontFamily: 'serif',
                      }}
                    />
                    {/* Normal DBP threshold: 80 */}
                    <ReferenceLine
                      y={80}
                      stroke="#1e3a8a"
                      strokeDasharray="3 3"
                      strokeWidth={0.8}
                      label={{
                        value: '舒張正常 (80)',
                        position: 'insideBottomRight',
                        fill: '#1e3a8a',
                        fontSize: 10,
                        fontFamily: 'serif',
                      }}
                    />
                  </>
                )}

                {/* Systolic Line */}
                {showSystolic && (
                  <Line
                    type="monotone"
                    dataKey="systolic"
                    name="收縮壓 (SYS)"
                    stroke="#991b1b"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#991b1b', stroke: '#fbf9f5', strokeWidth: 1.5 }}
                    activeDot={{ r: 6, fill: '#7f1d1d', stroke: '#fff', strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                )}

                {/* Diastolic Line */}
                {showDiastolic && (
                  <Line
                    type="monotone"
                    dataKey="diastolic"
                    name="舒張壓 (DIA)"
                    stroke="#1e3a8a"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#1e3a8a', stroke: '#fbf9f5', strokeWidth: 1.5 }}
                    activeDot={{ r: 6, fill: '#172554', stroke: '#fff', strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                )}

                {/* Pulse Line */}
                {showPulse && (
                  <Line
                    type="monotone"
                    dataKey="pulse"
                    name="心跳脈搏 (PULSE)"
                    stroke="#166534"
                    strokeWidth={2}
                    strokeDasharray="2 2"
                    dot={{ r: 3.5, fill: '#166534', stroke: '#fbf9f5', strokeWidth: 1.5 }}
                    activeDot={{ r: 5, fill: '#14532d', stroke: '#fff', strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            ) : (
              <AreaChart
                data={chartData}
                margin={{ top: 12, right: 16, left: -10, bottom: 6 }}
              >
                <defs>
                  <linearGradient id="sysGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#991b1b" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#991b1b" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="diaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="pulseGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#166534" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#166534" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#d6cebe"
                  vertical={false}
                />
                <XAxis
                  dataKey="displayLabel"
                  stroke="#78716c"
                  tick={{ fontSize: 11, fontFamily: 'monospace', fill: '#44403c' }}
                  tickLine={{ stroke: '#a8a29e' }}
                  axisLine={{ stroke: '#78716c' }}
                />
                <YAxis
                  domain={yDomain}
                  stroke="#78716c"
                  tick={{ fontSize: 11, fontFamily: 'monospace', fill: '#44403c' }}
                  tickLine={{ stroke: '#a8a29e' }}
                  axisLine={{ stroke: '#78716c' }}
                />
                <Tooltip content={renderCustomTooltip} />

                {/* AHA Reference Lines in Area Mode */}
                {showRefLines && (
                  <>
                    <ReferenceLine
                      y={120}
                      stroke="#ca8a04"
                      strokeDasharray="4 4"
                      strokeWidth={1}
                      label={{
                        value: '120',
                        position: 'right',
                        fill: '#854d0e',
                        fontSize: 10,
                      }}
                    />
                    <ReferenceLine
                      y={140}
                      stroke="#dc2626"
                      strokeDasharray="4 4"
                      strokeWidth={1.2}
                      label={{
                        value: '140',
                        position: 'right',
                        fill: '#991b1b',
                        fontSize: 10,
                      }}
                    />
                    <ReferenceLine
                      y={80}
                      stroke="#1e3a8a"
                      strokeDasharray="3 3"
                      strokeWidth={0.8}
                      label={{
                        value: '80',
                        position: 'right',
                        fill: '#1e3a8a',
                        fontSize: 10,
                      }}
                    />
                  </>
                )}

                {showSystolic && (
                  <Area
                    type="monotone"
                    dataKey="systolic"
                    stroke="#991b1b"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#sysGradient)"
                    isAnimationActive={false}
                  />
                )}
                {showDiastolic && (
                  <Area
                    type="monotone"
                    dataKey="diastolic"
                    stroke="#1e3a8a"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#diaGradient)"
                    isAnimationActive={false}
                  />
                )}
                {showPulse && (
                  <Area
                    type="monotone"
                    dataKey="pulse"
                    stroke="#166534"
                    strokeWidth={1.5}
                    fillOpacity={1}
                    fill="url(#pulseGradient)"
                    isAnimationActive={false}
                  />
                )}
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Chart Footer Guide */}
      <div className="mt-3 pt-2 border-t border-paper-300 flex flex-wrap items-center justify-between text-[11px] font-serif text-ink-500">
        <div className="flex items-center gap-3">
          <span>
            <strong className="text-red-900 font-mono font-bold">●</strong> 收縮壓 (SBP)
          </span>
          <span>
            <strong className="text-blue-900 font-mono font-bold">●</strong> 舒張壓 (DBP)
          </span>
          <span>
            <strong className="text-emerald-800 font-mono font-bold">●</strong> 脈搏 (HR)
          </span>
        </div>
        <div>
          <span>量測次數: <strong>{chartData.length}</strong> 筆數據點</span>
        </div>
      </div>
    </div>
  );
};
